import { useState } from "react";
import { ChevronRight, CircleDashed, Clock3, TriangleAlert } from "lucide-react";

import { LiveDot, parseDbTime } from "@nexus/widget-kit";
import { elapsedPercent, formatAgo, formatCount, formatPercent, formatSpan } from "./format";
import type { EventMonitorLabels } from "./labels";
import { eventSignals, rankLiveEvents, type MonitoredEvent } from "./model";
import { Sparkline } from "./Sparkline";

const VISIBLE_ROWS = 6;

/**
 * One column grid for the header and every row. Narrow widgets keep the event,
 * its detections and its signal; wider ones add response, targets and time.
 * On very wide screens the columns stop growing at a readable width and the
 * extra room is shared evenly between them, rather than pooling in one column.
 */
const GRID =
  "grid grid-cols-[minmax(0,1fr)_auto_2.75rem] items-center gap-x-4 @3xl/widget:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_4.5rem_4.5rem_minmax(6rem,1fr)_2.75rem] @[96rem]/widget:grid-cols-[minmax(0,30rem)_minmax(0,22rem)_6rem_6rem_minmax(0,16rem)_2.75rem] @[96rem]/widget:justify-between";
const WIDE = "hidden @3xl/widget:block";

/**
 * Events as a ranked list, details on click. Live: what needs attention comes
 * first, with the time left. Recent (nothing live): the latest ended events
 * first, so the system's performance stays readable between events.
 */
export function PerformanceView({
  events,
  now,
  labels,
  onOpen,
  phase = "live",
  limit = VISIBLE_ROWS,
}: {
  events: MonitoredEvent[];
  now: number;
  labels: EventMonitorLabels;
  onOpen?: (event: MonitoredEvent) => void;
  phase?: "live" | "recent";
  limit?: number;
}) {
  const [showAll, setShowAll] = useState(false);
  const ranked =
    phase === "live"
      ? rankLiveEvents(events, now)
      : events
          .map((event) => ({ event, ...eventSignals(event, now) }))
          .sort((a, b) => parseDbTime(b.event.event_end) - parseDbTime(a.event.event_end));
  const visible = showAll ? ranked : ranked.slice(0, limit);

  return (
    <div className="min-w-0">
      <div
        aria-hidden="true"
        className={`${GRID} border-b border-border px-2 pb-2 text-xs font-medium text-muted`}
      >
        <span>{phase === "live" ? labels.event : labels.recent}</span>
        <span>{labels.detections}</span>
        <span className={`${WIDE} text-right`}>{labels.responseCoverage}</span>
        <span className={`${WIDE} text-right`}>{labels.targets}</span>
        <span className={WIDE}>{phase === "live" ? labels.timeLeft : labels.ended}</span>
        <span />
      </div>
      <ul className="divide-y divide-border/70">
        {visible.map(({ event, summary, anomalies, quality: signal }) => {
          // An ended event's data is naturally old: only missing data is worth a flag.
          const quality = phase === "recent" && signal === "older" ? null : signal;
          return (
          <li key={event.event_id}>
            <button
              type="button"
              disabled={!onOpen}
              onClick={() => onOpen?.(event)}
              className={`${GRID} group w-full rounded-md px-2 py-2.5 text-left transition-colors enabled:hover:bg-surface-elevated/60 focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none`}
            >
              <span className="min-w-0">
                <span className="flex min-w-0 items-center gap-2">
                  {phase === "live" ? (
                    <LiveDot tone={anomalies ? "error" : "accent"} />
                  ) : (
                    <span
                      className={`size-2 shrink-0 rounded-full ${anomalies ? "bg-status-error" : "bg-border-strong"}`}
                      aria-hidden
                    />
                  )}
                  <span className="truncate text-sm font-semibold text-primary">
                    {event.event_label}
                  </span>
                  {quality ? (
                    <span
                      className="shrink-0 text-muted"
                      title={quality === "older" ? "Latest data is older than expected" : "Some channel data is missing"}
                    >
                      {quality === "older" ? (
                        <Clock3 className="size-3.5" aria-label="Older data" />
                      ) : (
                        <CircleDashed className="size-3.5" aria-label="Partial data" />
                      )}
                    </span>
                  ) : null}
                </span>
                <span className="mt-0.5 block truncate pl-4 text-xs text-muted">
                  {plural(event.protected_events.length, "channel")} ·{" "}
                  {plural(event.tenant_ids.length, "tenant")}
                </span>
              </span>
              <span className="flex min-w-0 items-center gap-3">
                <span className="text-base font-semibold tabular-nums text-primary @3xl/widget:w-14 @3xl/widget:shrink-0">
                  {formatCount(summary.detections)}
                </span>
                <span className="hidden max-w-40 min-w-12 flex-1 @3xl/widget:block @[96rem]/widget:max-w-56">
                  <Sparkline segments={event.segments} />
                </span>
              </span>
              <span className={`${WIDE} text-right text-sm font-medium tabular-nums text-primary`}>
                {formatPercent(summary.responseRate)}
              </span>
              <span
                className={`${WIDE} text-right text-sm tabular-nums text-secondary`}
                title="Channels that reached their final detection target"
              >
                {summary.targets ? `${summary.reached}/${summary.targets}` : "—"}
              </span>
              {phase === "recent" ? (
                <span className={`${WIDE} text-xs tabular-nums text-muted`}>
                  {formatAgo(parseDbTime(event.event_end), now)}
                </span>
              ) : (
              <span className={`${WIDE} min-w-0`}>
                <span className="block h-1 overflow-hidden rounded-full bg-border/70">
                  <span
                    className="block h-full rounded-full bg-brand-cyan"
                    style={{ width: `${elapsedPercent(event.event_start, event.event_end, now)}%` }}
                  />
                </span>
                <span className="mt-1 block text-xs tabular-nums text-muted">
                  {formatSpan(parseDbTime(event.event_end) - now)}
                </span>
              </span>
              )}
              <span className="flex items-center justify-end gap-1">
                {anomalies ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-status-error-bg px-1.5 py-0.5 text-xs font-semibold tabular-nums text-status-error-text"
                    title={`${anomalies} open ${anomalies === 1 ? "anomaly" : "anomalies"}`}
                  >
                    <TriangleAlert className="size-3" aria-hidden />
                    {anomalies}
                  </span>
                ) : null}
                <ChevronRight
                  className="size-4 shrink-0 text-muted transition-transform group-enabled:group-hover:translate-x-0.5 motion-reduce:transition-none"
                  aria-hidden
                />
              </span>
            </button>
          </li>
          );
        })}
      </ul>
      {ranked.length > limit ? (
        <button
          type="button"
          onClick={() => setShowAll((value) => !value)}
          className="mt-1 rounded-md px-2 py-1 text-xs font-medium text-secondary hover:bg-surface-elevated hover:text-primary focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none"
        >
          {showAll ? "Show less" : `Show ${ranked.length - limit} more`}
        </button>
      ) : null}
    </div>
  );
}

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
